import React, { useState, useMemo, useEffect } from "react";

import { Typography } from "@packages/ds";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { ACTIVITY_TYPES, ACTIVITY_CONTEXT } from "Constants/constants";
import Button, { Link } from "ds/Button";
import * as Sticker from "ds/Sticker";
import { Tooltip } from "ds/Tooltip";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize, getSubscriptionEditUrl } from "Libs/utils";
import {
  selectAccessesForSingleUser,
  selectAccessesAreLoading
} from "Reducers/access/environmentTypeAccess/environmentTypeAccess.selectors";
import { getSelectors, loadEnvironmentActivities } from "Reducers/activity";
import { meSelector } from "Reducers/app/selectors";
import {
  toggleEnvironmentActivation,
  environmentToggleActivationLoadingSelector,
  deleteEnvironment
} from "Reducers/environment";
import {
  selectDeletedEnvironment,
  selectUsedEnvironments
} from "Reducers/environment/environment.selectors";
import { getIntegrations, integrationsSelector } from "Reducers/integration";
import { subscriptionSelector } from "Reducers/subscription";
import { getMaxDevelopmentEnvironmentNumber } from "src/organization/pages/project/components/ProjectDetails/ProjectDetails";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "../EnvironmentGeneralSettings.styles";

import {
  DeleteEnvWithIntegration,
  NoDeletePermission,
  DeleteEnvWithoutIntegration,
  DeleteEnvModal,
  DeactivateEnvModal,
  DeactivateEnvSection,
  DeleteActiveEnv
} from "./StatusSettings";

import type { Environment, Organization, Project } from "@packages/client";

type StatusSettingProps = {
  closeModal: () => void;
  confirmDeactivateModal: boolean;
  isEnvActive: boolean;
  environment: Environment;
  project?: Project;
  openDeactivateConfirm: () => void;
  openDeleteConfirm: () => void;
  confirmDeleteModal: boolean;
  organization?: Organization;
  organizationId: string;
  projectId: string;
  environmentId: string;
};

const StatusSetting = ({
  isEnvActive,
  environment,
  closeModal,
  confirmDeactivateModal,
  project,
  confirmDeleteModal,
  openDeleteConfirm,
  openDeactivateConfirm,
  organization,
  organizationId,
  projectId,
  environmentId
}: StatusSettingProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [confirmEnvironmentName, setConfirmEnvironmentName] = useState("");
  const [allowActivityDispatch, setAllowActivityDispatch] = useState(true);

  useEffect(() => {
    dispatch(getIntegrations({ projectId }));
  }, [dispatch, projectId]);

  const hasDeleteLink = environment?.hasLink("#delete");
  const isEnvDeleted = useAppSelector(state =>
    selectDeletedEnvironment(state, { environmentId, projectId })
  );
  const userId = useAppSelector(meSelector)?.id;
  const userEnvRoles = useSelectorWithUrlParams(selectAccessesForSingleUser, {
    userId
  });
  const isAccessesLoading = useAppSelector(selectAccessesAreLoading);
  const environmentType = environment.type;
  const userEnvRole = userEnvRoles?.environments[environmentType].role;

  const isEnvToggleLoading = useSelectorWithUrlParams(
    environmentToggleActivationLoadingSelector
  );

  const hasActivateLink = environment.hasLink("#activate");

  const environmentHasCode = environment?.has_code;

  const environmentHasActivateLinkAndCode =
    hasActivateLink && environmentHasCode;

  const hasDeactivateLink = environment.hasLink("#deactivate");

  const isAdmin = userEnvRole === "admin";

  const usedEnvironments = useSelectorWithUrlParams(selectUsedEnvironments);

  const maxDevelopementEnvironmentNumber =
    getMaxDevelopmentEnvironmentNumber(project);
  const leftEnvs = maxDevelopementEnvironmentNumber - usedEnvironments.length;

  const environmentName = environment.name;
  const projectName = project?.title;

  const allowDelete = !!(
    environmentName && confirmEnvironmentName === environmentName.trim()
  );
  const integrations = useSelectorWithUrlParams(integrationsSelector);

  const vcsIntegrationType = useMemo(
    () =>
      capitalize(
        integrations.find(i =>
          ["github", "gitlab", "bitbucket"].includes(i.type)
        )?.type
      ),
    [integrations]
  );

  const subscription = useSelectorWithUrlParams(subscriptionSelector, {
    id: project?.getSubscriptionId()
  });

  const subscriptionEditUrl = useMemo(
    () => getSubscriptionEditUrl({ subscription }),
    [subscription]
  );

  useEffect(() => {
    if (isEnvDeleted) {
      organization && project
        ? navigate(`/${organization.name}/${project.id}`)
        : navigate("/");
    }
  }, [isEnvDeleted, navigate, organization, project]);

  const toggleEnvActivation = () => {
    // The status can be "inactive", "active", or "paused" during this dispatch
    // Activate "inactive" environments and deactivate "paused" and "active" environments
    const action =
      environment?.status === "inactive" ? "activate" : "deactivate";

    dispatch(
      toggleEnvironmentActivation({
        projectId,
        environmentId,
        organizationId,
        action
      })
    );
  };
  const toggleEnvironmentDelete = () => {
    dispatch(deleteEnvironment({ projectId, environmentId, organizationId }));
  };
  const notAdminButton = () => {
    return (
      <Tooltip
        onOpenChange={() => {}}
        tooltip={intl.formatMessage({
          id: "no_access_environment_reactivate"
        })}
        analyticId="no_access_environment_reactivate"
      >
        <Button
          analyticId="activate_button"
          onClick={toggleEnvActivation}
          data-testid="environment-basics-status-save-btn"
          disabled={!environmentHasActivateLinkAndCode}
        >
          {intl.formatMessage({
            id: "activate_button"
          })}
        </Button>
      </Tooltip>
    );
  };
  const adminButton = () => {
    return (
      <Button
        analyticId="activate_button"
        onClick={toggleEnvActivation}
        data-testid="environment-basics-status-save-btn"
        disabled={!environmentHasActivateLinkAndCode}
      >
        {intl.formatMessage({
          id: "activate_button"
        })}
      </Button>
    );
  };

  const { all, inProgress } = getSelectors(ACTIVITY_CONTEXT.Environment, {
    projectId,
    organizationId,
    environmentId
  });

  const allEnvironmentActivities = Object.values(useAppSelector(all));
  const inProgressEnvironmentActivities = Object.values(
    useAppSelector(inProgress)
  );

  useEffect(() => {
    if (!allEnvironmentActivities.length && allowActivityDispatch) {
      setAllowActivityDispatch(false);
      dispatch(
        loadEnvironmentActivities({
          projectDescriptionId: projectId,
          environmentDescriptionId: environmentId,
          organizationDescriptionId: organizationId,
          filterNames: [
            "all_type",
            "git",
            "merge",
            "sync",
            "branch",
            "backup",
            "redeploy",
            "certificate",
            "system",
            "source_operation",
            "domain",
            "integrations",
            "upgrade"
          ],
          context: ACTIVITY_CONTEXT.Environment
        })
      );
    }
  }, [
    allEnvironmentActivities.length,
    allowActivityDispatch,
    dispatch,
    projectId,
    environmentId,
    organizationId
  ]);

  const statusLabel = environmentStatus => {
    if (environmentStatus === ACTIVITY_TYPES.ENVIRONMENT.ACTIVATE) {
      return "status_reactivating.title";
    } else if (environmentStatus === ACTIVITY_TYPES.ENVIRONMENT.DEACTIVATE) {
      return "status_deactivating.title";
    }
    return "status_building.title";
  };

  const inProgressStatusTranslation = statusLabel(
    inProgressEnvironmentActivities[0]?.type ?? ""
  );

  if (isAccessesLoading) {
    return <S.Loading />;
  }
  return (
    <>
      {environment.is_main ? (
        <S.StatusWrapper>
          {isEnvActive ? (
            <>
              <S.Title weight="bold">
                <FormattedMessage
                  id="status_active.title"
                  values={{
                    b: txt => <strong>{txt}</strong>
                  }}
                />
              </S.Title>
              <Typography.BodyText className="status_active_description">
                {intl.formatMessage({
                  id: "status_active_description",
                  defaultMessage:
                    "Each git push to this environment triggers a new deployment."
                })}
              </Typography.BodyText>
              <Typography.BodyText className="description">
                <FormattedMessage
                  id="production_environment_text"
                  values={{
                    link: txt => (
                      <Link
                        analyticId="delete_branch_link"
                        href={intl.formatMessage({
                          id: "delete_branch_link"
                        })}
                      >
                        {txt}
                      </Link>
                    )
                  }}
                />
              </Typography.BodyText>
            </>
          ) : (
            <>
              {environment.status === "dirty" ? (
                <S.Loading />
              ) : (
                // When the production environment branch is inactive via the CLI or dedicated cluster
                <>
                  <S.Title>
                    <FormattedMessage
                      id="status_inactive.title"
                      values={{
                        b: txt => <strong>{txt}</strong>
                      }}
                    />
                  </S.Title>
                  <S.StatusSettingBodyText className="description">
                    <FormattedMessage
                      id="status_inactive_description"
                      values={{
                        link: txt => (
                          <Link
                            href={intl.formatMessage({
                              id: "delete_branch_link"
                            })}
                            analyticId="delete_branch_link"
                          >
                            {txt}
                          </Link>
                        )
                      }}
                    />
                  </S.StatusSettingBodyText>
                  <hr className="full-width" />
                  <S.Title weight="bold">
                    {intl.formatMessage({
                      id: "activate_environment",
                      defaultMessage: "Activate environment"
                    })}
                  </S.Title>
                  <Typography.BodyText className="activate-description">
                    {intl.formatMessage({
                      id: "status_activate_description",
                      defaultMessage:
                        "While the Git branch is available, activating will sync data from the parent environment and redeploy."
                    })}
                  </Typography.BodyText>

                  <S.ActivateButton
                    onClick={toggleEnvActivation}
                    analyticId="activate_button"
                    disabled={!environmentHasActivateLinkAndCode}
                  >
                    {intl.formatMessage({
                      id: "activate_button",
                      defaultMessage: "Activate"
                    })}
                  </S.ActivateButton>
                </>
              )}
            </>
          )}
        </S.StatusWrapper>
      ) : isEnvActive ? (
        <>
          <DeactivateEnvSection
            openDeactivateConfirm={openDeactivateConfirm}
            isAdmin={hasDeactivateLink}
            environmentTarget={environment?.deployment_target}
          />
          <DeactivateEnvModal
            confirmDeactivateModal={confirmDeactivateModal}
            closeModal={closeModal}
            environment={environment}
            project={project}
            toggleEnvironmentActivation={toggleEnvActivation}
          />
          <DeleteActiveEnv />
        </>
      ) : (
        <>
          {environment.status === "dirty" ? (
            <S.StatusWrapper>
              <S.Title>
                <FormattedMessage
                  id={inProgressStatusTranslation}
                  values={{
                    b: txt => <strong>{txt}</strong>
                  }}
                />
              </S.Title>
              <S.Loading />{" "}
            </S.StatusWrapper>
          ) : (
            <>
              <S.StatusWrapper>
                <DeleteEnvModal
                  projectName={projectName}
                  environmentName={environmentName}
                  confirmDeleteModal={confirmDeleteModal}
                  closeModal={closeModal}
                  confirmEnvironmentName={confirmEnvironmentName}
                  setConfirmEnvironmentName={setConfirmEnvironmentName}
                  allowDelete={allowDelete}
                  toggleEnvironmentDelete={toggleEnvironmentDelete}
                  project={project}
                />
                <S.Title>
                  <FormattedMessage
                    id="status_inactive.title"
                    values={{
                      b: txt => <strong>{txt}</strong>
                    }}
                  />
                </S.Title>
                <S.StatusSettingBodyText className="description">
                  <FormattedMessage
                    id="status_inactive_description"
                    values={{
                      link: txt => (
                        <Link
                          href={intl.formatMessage({
                            id: "delete_branch_link"
                          })}
                          analyticId="delete_branch_link"
                        >
                          {txt}
                        </Link>
                      )
                    }}
                  />
                </S.StatusSettingBodyText>
                {leftEnvs ? (
                  isAdmin ? (
                    adminButton()
                  ) : (
                    notAdminButton()
                  )
                ) : (
                  <>
                    <Sticker.Root priority="information">
                      <Sticker.Icon priority="information" />
                      <S.StickerText>
                        {intl.formatMessage({
                          id: "max_envs_limit"
                        })}
                      </S.StickerText>
                      <Button
                        to={subscriptionEditUrl?.url ?? ""}
                        disabled={!isAdmin}
                        analyticId="upgrade"
                      >
                        {intl.formatMessage({
                          id: "upgrade"
                        })}
                      </Button>
                    </Sticker.Root>
                  </>
                )}
              </S.StatusWrapper>
            </>
          )}
          {environment.status === "dirty" || isEnvToggleLoading ? null : (
            <S.StatusWrapper>
              {hasDeleteLink ? (
                <>
                  <S.WarningWrapper>
                    <S.ErrorIcon color="var(--mode-vector-critical-default)" />
                    <Typography.BodyText weight="bold">
                      {intl.formatMessage({
                        id: "status_active_delete.title"
                      })}
                    </Typography.BodyText>
                  </S.WarningWrapper>

                  {vcsIntegrationType ? (
                    <DeleteEnvWithIntegration
                      environment={environment}
                      integrationTypes={vcsIntegrationType}
                      project={project}
                    />
                  ) : (
                    <DeleteEnvWithoutIntegration
                      environment={environment}
                      openDeleteConfirm={openDeleteConfirm}
                    />
                  )}
                </>
              ) : (
                <>
                  {vcsIntegrationType && isAdmin ? (
                    <>
                      <S.WarningWrapper>
                        <S.ErrorIcon color="var(--mode-vector-critical-default)" />
                        <Typography.BodyText weight="bold">
                          {intl.formatMessage({
                            id: "status_active_delete.title"
                          })}
                        </Typography.BodyText>
                      </S.WarningWrapper>
                      <DeleteEnvWithIntegration
                        environment={environment}
                        integrationTypes={vcsIntegrationType}
                        project={project}
                      />
                    </>
                  ) : (
                    <>
                      <S.WarningWrapper>
                        <S.ErrorIcon color="var(--mode-vector-critical-default)" />
                        <Typography.BodyText weight="bold">
                          {intl.formatMessage({
                            id: "status_active_delete.title"
                          })}
                        </Typography.BodyText>
                      </S.WarningWrapper>
                      <NoDeletePermission />
                    </>
                  )}
                </>
              )}
            </S.StatusWrapper>
          )}
        </>
      )}
    </>
  );
};

export default StatusSetting;
